import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Lightbulb,
  AlertTriangle,
  BookOpen,
  ChevronDown,
  TrendingDown,
  Target
} from "lucide-react";

export default function TeacherInsights() {
  const { teacher } = useOutletContext();
  const [insights, setInsights] = useState({ struggling_topics: [], total_topics_analyzed: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedGrade, setSelectedGrade] = useState("");

  const assignedGrades = teacher?.assignments 
    ? Object.keys(teacher.assignments).filter(g => teacher.assignments[g]?.length > 0).sort((a, b) => parseInt(a) - parseInt(b))
    : [];

  useEffect(() => {
    fetchInsights();
  }, [selectedGrade]);

  const fetchInsights = async () => {
    setLoading(true);
    try {
      let url = `${API}/teacher/dashboard/insights`;
      if (selectedGrade) {
        url += `?grade=${selectedGrade}`;
      }
      
      const response = await fetch(url, {
        credentials: "include"
      });
      
      if (response.ok) {
        const data = await response.json();
        setInsights(data);
      }
    } catch (error) {
      console.error("Failed to fetch insights:", error);
    } finally {
      setLoading(false);
    }
  };

  const getErrorRateColor = (rate) => {
    if (rate >= 60) return "text-red-600";
    if (rate >= 40) return "text-orange-600";
    return "text-amber-600";
  };

  const getErrorRateBg = (rate) => {
    if (rate >= 60) return "bg-red-100";
    if (rate >= 40) return "bg-orange-100";
    return "bg-amber-100";
  };

  // Group topics by subject
  const topicsBySubject = insights.struggling_topics.reduce((acc, topic) => {
    const subject = topic.subject || "Other";
    if (!acc[subject]) acc[subject] = [];
    acc[subject].push(topic);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Lightbulb className="w-7 h-7 text-amber-500" />
            Content Insights
          </h1>
          <p className="text-gray-500 mt-1">
            Topics where students need extra help
          </p>
        </div>

        {/* Grade Filter */}
        <div className="relative">
          <select
            value={selectedGrade}
            onChange={(e) => setSelectedGrade(e.target.value)}
            className="appearance-none w-full md:w-48 px-4 py-2 pr-10 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Grades</option>
            {assignedGrades.map((grade) => (
              <option key={grade} value={grade}>Grade {grade}</option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-0 shadow-md">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{insights.total_topics_analyzed}</p>
              <p className="text-sm text-gray-500">Topics Analyzed</p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{insights.struggling_topics.length}</p>
              <p className="text-sm text-gray-500">Topics Needing Attention</p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
              <Target className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{Object.keys(topicsBySubject).length}</p>
              <p className="text-sm text-gray-500">Subjects Covered</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Struggling Topics */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 bg-gray-200 rounded-lg animate-pulse"></div>
          ))}
        </div>
      ) : Object.keys(topicsBySubject).length > 0 ? (
        <div className="space-y-6">
          {Object.entries(topicsBySubject)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([subject, topics]) => (
              <Card key={subject} className="border-0 shadow-md overflow-hidden">
                <CardHeader className="bg-gray-50 py-3 px-4">
                  <CardTitle className="text-base flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <BookOpen className="w-5 h-5 text-blue-600" />
                      {subject}
                    </span>
                    <Badge variant="secondary">{topics.length} topics</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y divide-gray-100">
                    {topics.map((topic, index) => (
                      <div 
                        key={index}
                        className="p-4 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`w-2 h-2 rounded-full ${
                                topic.error_rate >= 60 ? 'bg-red-500' :
                                topic.error_rate >= 40 ? 'bg-orange-500' :
                                'bg-amber-500'
                              }`}></span>
                              <p className="font-medium text-gray-900">{topic.topic}</p>
                            </div>
                            
                            {/* Progress Bar */}
                            <div className="flex items-center gap-3">
                              <div className="flex-1 bg-gray-200 rounded-full h-2 overflow-hidden">
                                <div 
                                  className={`h-full rounded-full ${
                                    topic.error_rate >= 60 ? 'bg-red-500' :
                                    topic.error_rate >= 40 ? 'bg-orange-500' :
                                    'bg-amber-500'
                                  }`}
                                  style={{ width: `${topic.error_rate}%` }}
                                ></div>
                              </div>
                              <span className={`text-sm font-medium ${getErrorRateColor(topic.error_rate)}`}>
                                {topic.error_rate}% error rate
                              </span>
                            </div>
                            
                            <p className="text-sm text-gray-500 mt-2">
                              {topic.correct_attempts} correct out of {topic.total_attempts} attempts
                            </p>
                          </div>

                          {/* Severity Badge */}
                          <div className={`px-3 py-1 rounded-full text-sm font-medium ${getErrorRateBg(topic.error_rate)} ${getErrorRateColor(topic.error_rate)}`}>
                            {topic.error_rate >= 60 ? 'Critical' :
                             topic.error_rate >= 40 ? 'High' : 'Moderate'}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
        </div>
      ) : (
        <Card className="border-0 shadow-md">
          <CardContent className="py-16 text-center">
            <Lightbulb className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Insights Available</h3>
            <p className="text-gray-500 max-w-md mx-auto">
              Insights will appear as students answer more questions. 
              Topics with error rates above 30% will be highlighted here.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Info Card */}
      <Card className="border-0 shadow-sm bg-amber-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">Understanding Error Rates</p>
              <p className="text-sm text-amber-700 mt-1">
                Topics appear here when students get more than 30% of questions wrong (minimum 5 attempts required).
                Focus your teaching on <span className="font-medium text-red-600">Critical</span> topics first,
                then move to <span className="font-medium text-orange-600">High</span> priority ones.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
